import React, { useMemo } from 'react';
import classNames from 'classnames';
import styles from './sparkline.module.sass';

export interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  strokeWidth?: number;
  className?: string;
}

const Sparkline = React.forwardRef<SVGSVGElement, SparklineProps>(
  (
    {
      data,
      width = 64,
      height = 16,
      color = 'var(--chart-1)',
      strokeWidth = 1.5,
      className,
    },
    ref
  ) => {
    const pathD = useMemo(() => {
      const valid = data.filter(Number.isFinite);
      if (valid.length === 0) return '';
      if (valid.length === 1) return `M ${width / 2} ${height / 2}`;

      const min = Math.min(...valid);
      const max = Math.max(...valid);
      const range = max - min || 1;

      const points = valid.map((value, index) => {
        const x = (index / (valid.length - 1)) * width;
        const y = height - ((value - min) / range) * height;
        return `${x},${y}`;
      });

      return `M ${points.join(' L ')}`;
    }, [data, width, height]);

    return (
      <svg
        ref={ref}
        className={classNames(styles.sparkline, className)}
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        preserveAspectRatio="none"
      >
        <polyline
          className={styles.line}
          points={(() => {
              const v = data.filter(Number.isFinite);
              if (v.length < 2) return '';
              const min = Math.min(...v);
              const max = Math.max(...v);
              const range = max - min || 1;
              return v.map((value, index) => {
                const x = (index / (v.length - 1)) * width;
                const y = height - ((value - min) / range) * height;
                return `${x},${y}`;
              }).join(' ');
            })()}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
);

Sparkline.displayName = 'Sparkline';

export default Sparkline;
